import { create } from 'zustand';
import { Product, Collection } from '../types';
import { api } from '../services/api';

interface Banner {
  id: string;
  title: string;
  imageUrl: string;
  linkUrl: string | null;
  linkType: string;
  position: number;
}

interface CatalogStore {
  products: Product[];
  collections: Collection[];
  banners: Banner[];
  loaded: boolean;
  loading: boolean;
  lastLoadedAt: number;
  preload: () => Promise<void>;
}

const STALE_MS = 60_000; // refresh data if older than 60s

export const useCatalogStore = create<CatalogStore>((set, get) => ({
  products: [],
  collections: [],
  banners: [],
  loaded: false,
  loading: false,
  lastLoadedAt: 0,

  preload: async () => {
    if (get().loading) return;
    const isStale = Date.now() - get().lastLoadedAt > STALE_MS;
    if (get().loaded && !isStale) return;
    set({ loading: true });
    try {
      // Двухволновая загрузка: сплэш прячется после быстрой первой порции (100 игр),
      // остальной каталог доезжает фоном — старт заметно быстрее.
      const [gamesRes, subsRes, collections, banners] = await Promise.all([
        api.getProducts({ category: 'game', limit: 100 }),
        api.getProducts({ category: 'subscription', limit: 50 }),
        api.getCollections(),
        api.getBanners(),
      ]);
      const products = [...gamesRes.products, ...subsRes.products];

      const productIds = new Set(products.map(p => p.id));
      const collectionProducts = collections.flatMap(c => c.products || []);
      for (const cp of collectionProducts) {
        if (!productIds.has(cp.id)) {
          products.push(cp);
          productIds.add(cp.id);
        }
      }

      set({ products, collections, banners, loaded: true, lastLoadedAt: Date.now() });

      // Вторая волна: полный каталог игр в фоне (для поиска/фильтров/полных списков)
      if (gamesRes.total > gamesRes.products.length) {
        api.getProducts({ category: 'game', limit: 500 })
          .then((fullRes) => {
            const cur = get();
            const merged = [...fullRes.products];
            const mergedIds = new Set(merged.map(p => p.id));
            for (const p of cur.products) {
              if (!mergedIds.has(p.id)) {
                merged.push(p);
                mergedIds.add(p.id);
              }
            }
            set({ products: merged, lastLoadedAt: Date.now() });
          })
          .catch(() => { /* фон — не критично, останется первая сотня */ });
      }
    } catch {
      // silent fail — HomePage will retry
    } finally {
      set({ loading: false });
    }
  },
}));
